import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/ui/Header';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Icon from '../components/AppIcon';
import SuccessNotification from '../components/ui/SuccessNotification';
import userService from '../services/userService';

const MyAccountPage = () => {
  const navigate = useNavigate();
  const { user, userProfile, updateUserProfile, signOut, resetPassword } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    fullName: ''
  });
  const [resetEmail, setResetEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [error, setError] = useState(null);
  const [resetError, setResetError] = useState(null);
  const [photoError, setPhotoError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showResetSection, setShowResetSection] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    if (user && userProfile) {
      setFormData({
        email: user?.email || '',
        username: userProfile?.username || '',
        fullName: userProfile?.full_name || ''
      });
      setPhotoPreview(userProfile?.profile_photo_url);
    }
  }, [user, userProfile]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Validate email
      if (!formData?.email?.trim()) {
        throw new Error('Email is required');
      }

      if (!formData?.email?.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      // Update user profile
      const { error: updateError } = await updateUserProfile(user?.id, {
        username: formData?.username?.trim(),
        full_name: formData?.fullName?.trim()
      });

      if (updateError) {
        throw new Error(updateError?.message || 'Failed to update profile');
      }

      // Note: Email update would typically require additional verification
      // For now, we'll just update the profile fields setSuccessMessage('Profile updated successfully!');
      setShowSuccess(true);

    } catch (err) {
      console.error('Update profile error:', err);
      setError(err?.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setPhotoError('Please select a valid image file');
      return;
    }

    // Validate file size (5MB)
    if (file?.size > 5242880) {
      setPhotoError('Image size must be less than 5MB');
      return;
    }

    setIsUploadingPhoto(true);
    setPhotoError(null);

    try {
      // Upload headshot
      const { photoUrl, error: uploadError } = await userService?.uploadHeadshot(user?.id, file);

      if (uploadError) {
        throw new Error(uploadError?.message || 'Failed to upload headshot');
      }

      // Update preview
      setPhotoPreview(photoUrl);

      // Show success message
      setSuccessMessage('Headshot uploaded successfully!');
      setShowSuccess(true);

    } catch (err) {
      console.error('Upload headshot error:', err);
      setPhotoError(err?.message || 'Failed to upload headshot. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e?.preventDefault();
    setIsResettingPassword(true);
    setResetError(null);

    try {
      // Validate email
      if (!resetEmail?.trim()) {
        throw new Error('Email is required for password reset');
      }

      if (!resetEmail?.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      // Send password reset email
      const { error: resetErr } = await resetPassword(resetEmail?.trim());

      if (resetErr) {
        throw new Error(resetErr?.message || 'Failed to send reset email');
      }

      setSuccessMessage('Password reset email sent! Please check your email inbox.');
      setShowSuccess(true);
      setResetEmail('');
      setShowResetSection(false);

    } catch (err) {
      console.error('Password reset error:', err);
      setResetError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleLogOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-2xl">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-8 text-center">
            <Icon name="Lock" size={48} className="text-blue-200 mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold text-white mb-2">
              Access Restricted
            </h2>
            <p className="text-blue-100 mb-6">
              Please log in to access your account settings.
            </p>
            <Button
              variant="default"
              onClick={() => navigate('/auth/login')}
              iconName="LogIn"
              iconPosition="left"
              className="bg-white text-indigo-900 hover:bg-gray-100"
            >
              Go to Login
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Account - RoleCall</title>
        <meta name="description" content="Manage your account settings and profile information" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-2xl">
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-xl text-blue-200 hover:text-white hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={24} />
              </button>
              <div>
                <h1 className="text-3xl font-heading font-bold text-white">
                  My Account
                </h1>
                <p className="text-blue-100 font-body mt-1">
                  Manage your account settings and profile information
                </p>
              </div>
            </div>
          </div>

          {/* Success Notification */}
          {showSuccess && (
            <SuccessNotification
              message={successMessage}
              isVisible={showSuccess}
              onClose={() => setShowSuccess(false)}
            />
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-red-800" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Profile Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Headshot Upload Section */}
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
              <h2 className="text-xl font-heading font-semibold text-white mb-6 flex items-center space-x-2">
                <Icon name="Camera" size={24} />
                <span>Profile Photo</span>
              </h2>

              <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6">
                {/* Photo Preview */}
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-white bg-opacity-10 border-4 border-white border-opacity-30">
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/assets/images/no_image.png';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="User" size={48} className="text-white opacity-50" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Upload Controls */}
                <div className="flex-grow">
                  <p className="text-white text-sm mb-3">
                    Upload a professional headshot to personalize your profile. Accepted formats: JPG, PNG, WebP (Max 5MB)
                  </p>

                  {photoError && (
                    <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-lg p-3 mb-3">
                      <p className="text-red-800 text-sm">{photoError}</p>
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    <label htmlFor="headshot-upload" className="cursor-pointer">
                      <div className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isUploadingPhoto
                          ? 'bg-gray-400 cursor-not-allowed' :'bg-purple-600 hover:bg-purple-700 text-white'
                      }`}>
                        <div className="flex items-center space-x-2">
                          <Icon name="Upload" size={16} />
                          <span>{isUploadingPhoto ? 'Uploading...' : 'Choose Photo'}</span>
                        </div>
                      </div>
                      <input
                        id="headshot-upload"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handlePhotoUpload}
                        disabled={isUploadingPhoto}
                        className="hidden"
                      />
                    </label>

                    {photoPreview && (
                      <button
                        type="button"
                        onClick={() => {
                          setPhotoPreview(null);
                          setPhotoError(null);
                          document.getElementById('headshot-upload').value = '';
                        }}
                        className="px-4 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 text-white rounded-lg text-sm font-medium transition-colors"
                        disabled={isUploadingPhoto}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Information */}
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
              <h2 className="text-xl font-heading font-semibold text-white mb-6 flex items-center space-x-2">
                <Icon name="User" size={24} />
                <span>Profile Information</span>
              </h2>

              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    placeholder="Enter your email address"
                    required
                    disabled={isLoading}
                    className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                  />
                  <p className="text-xs text-blue-200 mt-1">
                    Email changes require verification and may take some time to process.
                  </p>
                </div>

                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-white mb-2">
                    Username
                  </label>
                  <Input
                    id="username"
                    type="text"
                    value={formData?.username}
                    onChange={(e) => handleInputChange('username', e?.target?.value)}
                    placeholder="Enter your username"
                    disabled={isLoading}
                    className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                  />
                </div>

                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-white mb-2">
                    Full Name
                  </label>
                  <Input
                    id="fullName"
                    type="text"
                    value={formData?.fullName}
                    onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                    placeholder="Enter your full name"
                    disabled={isLoading}
                    className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                  />
                </div>
              </div>
            </div>

            {/* Password Reset Section */}
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-heading font-semibold text-white flex items-center space-x-2">
                  <Icon name="Key" size={24} />
                  <span>Password Reset</span>
                </h2>
                
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowResetSection(!showResetSection)}
                  iconName={showResetSection ? "ChevronUp" : "ChevronDown"}
                  iconPosition="right"
                  className="border-white text-white hover:bg-white hover:text-indigo-900"
                >
                  {showResetSection ? 'Hide' : 'Reset Password'}
                </Button>
              </div>

              {showResetSection && (
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  {resetError && (
                    <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <Icon name="AlertTriangle" size={20} className="text-red-800" />
                        <p className="text-red-800 font-medium">{resetError}</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="resetEmail" className="block text-sm font-medium text-white mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="resetEmail"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => {
                        setResetEmail(e?.target?.value);
                        if (resetError) setResetError(null);
                      }}
                      placeholder="Enter your email to reset password"
                      required
                      disabled={isResettingPassword}
                      className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
                    />
                    <p className="text-xs text-blue-200 mt-1">
                      You will receive an email with instructions to reset your password.
                    </p>
                  </div>

                  <div className="flex items-center space-x-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowResetSection(false);
                        setResetEmail('');
                        setResetError(null);
                      }}
                      className="border-white text-white hover:bg-white hover:text-indigo-900"
                    >
                      Cancel
                    </Button>
                    
                    <Button
                      type="submit"
                      variant="default"
                      size="sm"
                      loading={isResettingPassword}
                      iconName="Mail"
                      iconPosition="left"
                      className="bg-blue-600 text-white hover:bg-blue-700"
                      disabled={isResettingPassword}
                    >
                      {isResettingPassword ? 'Sending...' : 'Send Reset Email'}
                    </Button>
                  </div>
                </form>
              )}

              {!showResetSection && (
                <p className="text-blue-100 text-sm">
                  Click "Reset Password" to send a password reset email to your registered email address.
                </p>
              )}
            </div>

            {/* Account Info */}
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
              <h2 className="text-xl font-heading font-semibold text-white mb-4 flex items-center space-x-2">
                <Icon name="Info" size={24} />
                <span>Account Information</span>
              </h2>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-blue-200">Role:</span>
                  <span className="font-medium text-white capitalize">
                    {userProfile?.role || 'User'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-blue-200">Member since:</span>
                  <span className="font-medium text-white">
                    {userProfile?.created_at 
                      ? new Date(userProfile?.created_at)?.toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })
                      : 'Unknown'
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-blue-200">Last updated:</span>
                  <span className="font-medium text-white">
                    {userProfile?.updated_at 
                      ? new Date(userProfile?.updated_at)?.toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })
                      : 'Never'
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleLogOut}
                  iconName="LogOut"
                  iconPosition="left"
                  className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-indigo-900"
                >
                  Log Out
                </Button>
                
                <Button
                  type="submit"
                  variant="default"
                  loading={isLoading}
                  iconName="Save"
                  iconPosition="left"
                  className="w-full sm:w-auto bg-white text-indigo-900 hover:bg-gray-100"
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating Profile...' : 'Update Profile'}
                </Button>
              </div>
            </div>
          </form>
        </main>
      </div>
    </>
  );
};

export default MyAccountPage;